import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ip } from 'src/app/constant/constant';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { DataService } from 'src/app/services/data.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonCharacterImageUploadModalComponent } from '../shared/common-character-image-upload-modal/common-character-image-upload-modal.component';
import { SubscriptionStatus } from '../subscription/models/subscription-usage.model';
import { ViewportService } from 'src/app/services/viewport.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-character-portfolio',
  templateUrl: './character-portfolio.component.html',
  styleUrls: ['./character-portfolio.component.scss'],
  standalone: false
})
export class CharacterPortfolioComponent implements OnInit, OnDestroy {
  mode: any;
  imageurl = ip;
  deadCharacters: any[] = [];
  activeCharData: any;
  gamename: any;
  user_id: any;
  map_id: any;
  game_id: any;
  disablebtn: boolean = false;
  disableplaybtn: boolean = false;
  currentDate: string;
  selectedTab: string = 'active';
  activePath: string;
  retiredCharacters: any[] = [];
  planUsage: SubscriptionStatus | null = null;

  isClassAdded = false;
  viewMode: 'cards' | 'rows' = 'cards';
  isMobile = false;
  /** The choice made on a wide screen, restored when leaving mobile widths. */
  private preferredViewMode: 'cards' | 'rows' = 'cards';
  private viewportSubscription: Subscription;

  setViewMode(mode: 'cards' | 'rows') {
    this.preferredViewMode = mode;
    this.viewMode = mode;
  }

  get visibleChars(): any[] {
    if (this.selectedTab === 'retired') return this.retiredCharacters || [];
    if (this.selectedTab === 'dead') return this.deadCharacters || [];
    return this.activeCharData || [];
  }

  /** status pill for a character: in-session / awaiting / unassigned / retired / dead */
  charStatus(item: any): string {
    if (this.selectedTab === 'retired' || item?.is_retired) return 'retired';
    if (this.selectedTab === 'dead' || item?.is_dead) return 'dead';
    const game = item?.game_details?.[0];
    if (!game || !game.game_name) return 'unassigned';
    return game.is_active ? 'in-session' : 'awaiting';
  }

  charStatusLabel(item: any): string {
    return (
      {
        'in-session': 'In session',
        awaiting: 'Awaiting session',
        unassigned: 'Unassigned',
        retired: 'Retired',
        dead: 'Dead',
      } as Record<string, string>
    )[this.charStatus(item)];
  }

  /**
   * Play opens the game's map, or, for a character that isn't in a game,
   * its own map (solo play). Only a character with neither can't be played.
   * (It used to be disabled for every character outside a game.)
   */
  canPlay(item: any): boolean {
    return !!(item?.game_details?.[0]?.map_id || item?.map_code);
  }

  charLocation(item: any): string {
    const map = item?.map_name || item?.game_details?.[0]?.map_name;
    const game = item?.game_details?.[0]?.game_name;
    return [map, game].filter(Boolean).join(' · ') || 'No game yet';
  }

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private dataserv: DataService,
    private currentUserService: CurrentUserService,
    private toasterService: ToastService,
    private viewport: ViewportService,
    private cdr: ChangeDetectorRef,
  ) {
    this.isClassAdded = true;
  }

  ngOnInit(): void {
    this.viewportSubscription = this.viewport.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
      this.viewMode = isMobile ? 'cards' : this.preferredViewMode;
      this.cdr.detectChanges();
    });
    this.activePath = localStorage.getItem('activePath');
    this.currentUserService.currentUser.subscribe((data) => {
      if (data) {
        this.user_id = data.id;
        if (this.user_id) {
          this.disablebtn = false;
        } else {
          this.disablebtn = true;
        }
        this.getcharlist();
        this.cdr.detectChanges();
      }
    });
    this.currentUserService.planUsageData.subscribe(
      (data: SubscriptionStatus) => {
        if (data) {
          this.planUsage = data;
          this.cdr.detectChanges();
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.viewportSubscription?.unsubscribe();
  }

  editplayerdetail(data: any) {
    const playerId = data.id; // Make sure 'id' is a valid property of the 'data' object
    this.router.navigate(['/player-detail', playerId], {
      queryParams: { mode: 'edit' },
    });
  }

  imguploadopenmodel(data: any) {
    const addmodalRef = this.modalService.open(
      CommonCharacterImageUploadModalComponent,
      {
        size: 'md',
        backdrop: 'static',
        centered: true,
      },
    );

    addmodalRef.componentInstance.selectedImage = ip + data.regular_image;
    addmodalRef.componentInstance.char_id = data.id;
    addmodalRef.result.then((res) => {
      if (res) {
        this.getcharlist();
      }
      this.cdr.detectChanges();
    });
  }

  getcharlist() {
    this.activeCharData = [];
    this.gamename = [];

    if (!this.user_id) {
      console.error('User ID is not defined');
      return;
    }

    this.dataserv.Getcharacterdetail(this.user_id).subscribe(
      (res: any) => {
        if (!res || !res.character_details) {
          console.error('Invalid response from Getcharacterdetail');
          return;
        }

        const data = res.character_details || [];
        this.activeCharData = data.filter(
          (item: any) => !item.is_retired && !item.is_dead,
        );
        this.retiredCharacters = data.filter(
          (item: any) => item.is_retired === true,
        );
        this.deadCharacters = data.filter((item: any) => item.is_dead === true);
        this.cdr.detectChanges();
      },
      (error) => {
        this.toasterService.showSuccess('Something went wrong. Please try again.', 'Error');
        this.cdr.detectChanges();
      },
    );
  }

  playgame(charDetails: any) {
    this.map_id =
      charDetails.game_details.length > 0
        ? charDetails.game_details[0].map_id
        : charDetails.map_code;
    this.game_id =
      charDetails.game_details.length > 0
        ? charDetails.game_details[0].game_id
        : undefined;
    const today = new Date();
    this.currentDate = today.toISOString().slice(0, 10);

    if (charDetails.city_id && charDetails.city_id.length > 0) {
      this.router.navigate(
        [
          '/city-map-view/' +
          charDetails.city_id +
          '/' +
          this.map_id +
          '/' +
          (this.game_id ? this.game_id : 'null') +
          '/' +
          (charDetails.id ? charDetails.id : 'null'),
        ],
        {
          queryParams: {
            token: charDetails.cropped_image,
            isLoadToken: true,
            isPlaceToken: charDetails.current_location,
            backRoute: 'map-view-player',
            isMovable:
              !(
                charDetails.game_details[0]?.is_active ||
                charDetails.is_retired ||
                charDetails.is_dead
              ) || false,
          },
        },
      );
    } else {
      this.router.navigate(
        [
          '/map-view-player/' +
          this.map_id +
          '/' +
          (this.game_id ? this.game_id : 'null') +
          '/' +
          charDetails.id,
        ],
        {
          queryParams: {
            loadMainMap: true,
          },
        },
      );
    }
  }

  addnewplayer() {
    if (this.user_id) {
      this.router.navigate(['/player-detail/create'], {
        queryParams: { mode: 'create' },
      });
    } else {
      this.router.navigate(['/login']);
    }
  }

  isButtonDisabled(): boolean {
    if (!this.planUsage) return false;
    if (this.planUsage.subscription_features.max_active_characters === -1)
      return false;
    if (this.planUsage) {
      return (
        this.planUsage.subscription_features.max_active_characters <=
        this.planUsage.usage.characters_created
      );
    }
    return false;
  }

  shouldLimitReachedRetried(): boolean {
    if (!this.planUsage) return false;
    if (this.planUsage.subscription_features.max_retired_npcs === -1)
      return false;
    if (this.planUsage) {
      return (
        this.planUsage.subscription_features.max_retired_npcs <=
        this.planUsage.usage.retired_npcs_created
      );
    }
    return false;
  }
}
